import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { fetchAllHealth, fetchAllDashboards, fetchOpsStatus, fetchRealProjects } from '../services/api'
import { milestones, getMilestoneStats, getOverallStats } from '../data/milestones'
import ProjectsSection from '../components/ProjectsSection'

// ─── Animated Counter Hook ───
function useAnimatedNumber(target, duration = 1200) {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (!target && target !== 0) return
    const num = typeof target === 'number' ? target : parseFloat(target) || 0
    let start = 0
    const startTime = Date.now()
    const tick = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * num))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return value
}

// ─── SVG Ring Progress ───
function ProgressRing({ progress, size = 80, strokeWidth = 6, color, glowColor }) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)',
          filter: `drop-shadow(0 0 6px ${glowColor || color}40)` }} />
    </svg>
  )
}

// ─── Status Dot ───
function StatusDot({ status }) {
  const colors = {
    healthy: 'bg-emerald-400 shadow-emerald-400/50',
    online: 'bg-emerald-400 shadow-emerald-400/50',
    degraded: 'bg-amber-400 shadow-amber-400/50',
    down: 'bg-red-400 shadow-red-400/50',
  }
  return (
    <span className={`inline-block w-2.5 h-2.5 rounded-full shadow-lg ${colors[status] || 'bg-gray-500'}
      ${status === 'healthy' || status === 'online' ? 'animate-pulse' : ''}`} />
  )
}

// ─── Glass Card ───
function GlassCard({ children, className = '', glow, delay = 0 }) {
  return (
    <div
      className={`relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06]
        hover:border-white/[0.12] transition-all duration-500 hover:shadow-2xl ${className}`}
      style={{
        animation: `fadeInUp 0.6s ease-out ${delay}ms both`,
        ...(glow ? { boxShadow: `0 0 40px -12px ${glow}` } : {}),
      }}
    >
      {children}
    </div>
  )
}

// ─── Product Card ───
function ProductCard({ product, health, data, delay }) {
  const stats = milestones[product.id] ? getMilestoneStats(milestones[product.id]) : { avgProgress: 0, total: 0, done: 0 }
  const progress = useAnimatedNumber(stats.avgProgress)
  const statusMap = { healthy: 'يعمل', degraded: 'بطيء', down: 'متوقف' }
  const liveStatus = health?.status || 'down'

  return (
    <GlassCard delay={delay} glow={product.color + '15'}
      className="p-5 group cursor-default">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-2xl w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: `${product.color}15`, border: `1px solid ${product.color}25` }}>
            {product.icon}
          </div>
          <div>
            <h3 className="text-[15px] font-bold text-white">{product.nameAr}</h3>
            <p className="text-[11px] text-[#64748B] mt-0.5">{product.subtitleAr}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <StatusDot status={liveStatus} />
          <span className={liveStatus === 'healthy' ? 'text-emerald-400' : liveStatus === 'degraded' ? 'text-amber-400' : 'text-red-400'}>
            {statusMap[liveStatus] || '—'}
          </span>
        </div>
      </div>

      {/* Progress Ring + Metrics */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-shrink-0">
          <ProgressRing progress={progress} size={64} strokeWidth={5}
            color={product.color} glowColor={product.color} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-bold" style={{ color: product.light }}>{progress}%</span>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-2">
          {product.metrics.map(m => (
            <div key={m.key} className="bg-white/[0.03] rounded-lg px-2.5 py-1.5">
              <div className="text-[10px] text-[#64748B]">{m.label}</div>
              <div className="text-sm font-bold text-white">
                {data?.[m.key] ?? product.staticData?.[m.key] ?? '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Milestones Mini */}
      <div className="flex gap-1.5">
        {product.versions?.map((v, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/[0.06]">
            <div className="h-full rounded-full transition-all duration-1000"
              style={{
                width: v.status === 'done' ? '100%' : v.status === 'active' ? '60%' : '0%',
                background: v.status === 'done' ? product.color : v.status === 'active' ? `${product.color}80` : 'transparent',
              }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1">
        {product.versions?.map((v, i) => (
          <span key={i} className={`text-[9px] ${v.status === 'done' ? 'text-emerald-400' : v.status === 'active' ? 'text-white' : 'text-[#475569]'}`}>
            {v.label}
          </span>
        ))}
      </div>

      {/* Link */}
      {product.url && (
        <a href={product.url} target="_blank" rel="noopener noreferrer"
          className="mt-3 flex items-center gap-1 text-[11px] opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: product.color }}>
          فتح المشروع ←
        </a>
      )}
    </GlassCard>
  )
}

// ─── Hero Stat ───
function HeroStat({ icon, label, value, suffix, color, delay }) {
  const num = useAnimatedNumber(typeof value === 'number' ? value : 0)
  return (
    <GlassCard delay={delay} className="p-4 text-center" glow={color + '10'}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-2xl font-bold text-white">
        {typeof value === 'number' ? num.toLocaleString('ar-SA') : value}
        {suffix && <span className="text-sm text-[#64748B] mr-1">{suffix}</span>}
      </div>
      <div className="text-[11px] text-[#94A3B8] mt-1">{label}</div>
    </GlassCard>
  )
}

// ─── Agent Activity Feed ───
function AgentFeed({ activities, delay }) {
  return (
    <GlassCard delay={delay} className="p-5" glow="#8B5CF610">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-base">🤖</span> آخر نشاط للوكلاء
      </h3>
      <div className="space-y-3">
        {(activities || []).slice(0, 6).map((a, i) => (
          <div key={i} className="flex items-start gap-3 group">
            <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.06]
              flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
              {a.icon || '🤖'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-[#E2E8F0] leading-snug truncate">{a.agent}</p>
              <p className="text-[11px] text-[#64748B] truncate">{a.action}</p>
            </div>
            <span className="text-[10px] text-[#475569] flex-shrink-0 mt-1">{a.time}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

// ─── Financial Card ───
function FinancialCard({ delay }) {
  return (
    <GlassCard delay={delay} className="p-5" glow="#22C55E10">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-base">💰</span> النظرة المالية
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/[0.03] rounded-xl p-3 text-center">
          <div className="text-[10px] text-[#64748B] mb-1">الاحتراق الشهري</div>
          <div className="text-lg font-bold text-emerald-400">SAR 0</div>
          <div className="text-[9px] text-[#475569]">وكلاء فقط — تكلفة صفر</div>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-3 text-center">
          <div className="text-[10px] text-[#64748B] mb-1">الإيرادات المتوقعة</div>
          <div className="text-lg font-bold text-white">١٥٠ ألف</div>
          <div className="text-[9px] text-[#475569]">١٥ عميل / شهر</div>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-3 text-center">
          <div className="text-[10px] text-[#64748B] mb-1">نقطة التعادل</div>
          <div className="text-lg font-bold text-amber-400">١٥ عميل</div>
          <div className="text-[9px] text-[#475569]">الحالي: ٠</div>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-3 text-center">
          <div className="text-[10px] text-[#64748B] mb-1">مدة التشغيل</div>
          <div className="text-lg font-bold text-dashboard-light">∞</div>
          <div className="text-[9px] text-[#475569]">لا حاجة لتمويل</div>
        </div>
      </div>
    </GlassCard>
  )
}

// ─── Blockers Card ───
function BlockersCard({ delay }) {
  const blockers = [
    { icon: '🚨', title: 'Google Maps API Key', priority: 'P0', product: 'رفيق', color: '#EF4444' },
    { icon: '📋', title: 'استراتيجية إطلاق بوابة العمالة', priority: 'P1', product: 'بوابة العمالة', color: '#F59E0B' },
    { icon: '💰', title: 'تسعير موافق النهائي', priority: 'P1', product: 'موافق', color: '#F59E0B' },
  ]
  return (
    <GlassCard delay={delay} className="p-5" glow="#EF444410">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-base">🚨</span> المعوقات الحرجة
        <span className="text-[10px] bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full mr-auto">
          {blockers.length}
        </span>
      </h3>
      <div className="space-y-3">
        {blockers.map((b, i) => (
          <div key={i} className="flex items-center gap-3 bg-white/[0.02] rounded-xl p-3 border-r-2"
            style={{ borderRightColor: b.color }}>
            <span className="text-lg">{b.icon}</span>
            <div className="flex-1">
              <p className="text-[13px] text-white font-medium">{b.title}</p>
              <p className="text-[10px] text-[#64748B]">{b.product}</p>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${b.color}20`, color: b.color }}>
              {b.priority}
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

// ─── Milestones Timeline ───
function MilestonesTimeline({ delay }) {
  const items = [
    { title: 'لوحة إدارة الويب', product: 'بوابة العمالة', date: '١٧ أبريل', confidence: 'عالية', color: '#FF6B35' },
    { title: 'تكامل Google Maps', product: 'رفيق', date: '١٥ أبريل', confidence: 'محجوب ⛔', color: '#EF4444' },
    { title: 'تقارير العيادات', product: 'صحة', date: '١٨ أبريل', confidence: 'متوسطة', color: '#448AFF' },
    { title: 'جمع الأدلة التلقائي', product: 'موافق', date: '٢٠ أبريل', confidence: 'عالية', color: '#4CAF50' },
  ]
  return (
    <GlassCard delay={delay} className="p-5" glow="#8B5CF610">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-base">🎯</span> المعالم القادمة
      </h3>
      <div className="space-y-0 relative">
        <div className="absolute right-[15px] top-2 bottom-2 w-px bg-white/[0.06]" />
        {items.map((m, i) => (
          <div key={i} className="flex items-start gap-3 py-2.5 relative">
            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 z-10 text-xs"
              style={{ background: `${m.color}20`, border: `2px solid ${m.color}40` }}>
              {m.confidence.includes('⛔') ? '⛔' : '🎯'}
            </div>
            <div className="flex-1">
              <p className="text-[13px] text-white font-medium">{m.title}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-[#64748B]">{m.product}</span>
                <span className="text-[10px] text-[#94A3B8]">•</span>
                <span className="text-[10px] text-[#94A3B8]">{m.date}</span>
              </div>
            </div>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.04] text-[#94A3B8] flex-shrink-0 mt-1">
              {m.confidence}
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

// ─── Resource Allocation ───
function ResourceAllocation({ delay }) {
  const resources = [
    { name: 'بوابة العمالة', agents: 3, tokens: '٣٧٥ ألف', pct: 31, color: '#FF6B35' },
    { name: 'رفيق', agents: 2, tokens: '٢٩٤ ألف', pct: 25, color: '#00BFA5' },
    { name: 'موافق', agents: 3, tokens: '٢٢٥ ألف', pct: 19, color: '#4CAF50' },
    { name: 'صحة', agents: 2, tokens: '١٩٥ ألف', pct: 16, color: '#448AFF' },
    { name: 'لوحة التحكم', agents: 2, tokens: '٩٦ ألف', pct: 8, color: '#8B5CF6' },
  ]
  return (
    <GlassCard delay={delay} className="p-5" glow="#F59E0B10">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
        <span className="text-base">⚡</span> توزيع الموارد
      </h3>
      <div className="space-y-3">
        {resources.map((r, i) => (
          <div key={i}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[12px] text-[#E2E8F0]">{r.name}</span>
              <span className="text-[10px] text-[#64748B]">{r.agents} وكلاء • {r.tokens}</span>
            </div>
            <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${r.pct}%`, background: r.color }} />
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

// ═══════════════════════════════════════
//  MAIN DASHBOARD V2 COMPONENT
// ═══════════════════════════════════════
export default function DashboardV2() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [healthData, setHealthData] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [opsData, setOpsData] = useState(null)
  const [projectsData, setProjectsData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [activeSection, setActiveSection] = useState('overview')

  const loadData = useCallback(async () => {
    const token = localStorage.getItem('token')
    const [health, dash, ops, projects] = await Promise.all([
      fetchAllHealth(token),
      fetchAllDashboards(token),
      fetchOpsStatus(),
      fetchRealProjects(),
    ])
    setHealthData(health)
    setDashboardData(dash)
    setOpsData(ops)
    setProjectsData(projects)
    setLastUpdate(new Date())
    setLoading(false)
  }, [])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [loadData])

  const overallStats = getOverallStats()

  // Real stats from API
  const realProjects = projectsData?.projects || {}
  const activeServices = Object.values(realProjects).filter(p => p.api?.online).length
  const totalSourceLines = Object.values(realProjects).reduce((s, p) => s + (p.source?.lines || 0), 0)

  const PRODUCTS = [
    {
      id: 0, icon: '🚗', nameAr: 'رفيق', subtitleAr: 'تطبيق رفيق للسائقين',
      color: '#00BFA5', light: '#00E5CC', url: 'https://app.sanajim.com',
      metrics: [
        { key: 'activeMissions', label: 'مهام نشطة' },
        { key: 'totalMissions', label: 'إجمالي المهام' },
        { key: 'driverCount', label: 'سائقين' },
        { key: 'employerCount', label: 'جهات' },
      ],
      versions: [{ label: 'V1.0', status: 'done' }, { label: 'V2.0', status: 'active' }, { label: 'V3.0', status: 'planned' }],
      dataKey: 'rafiq',
    },
    {
      id: 1, icon: '✅', nameAr: 'موافق', subtitleAr: 'منصة جاهزية الامتثال',
      color: '#4CAF50', light: '#81C784', url: 'https://muwafaq.sanajim.com',
      metrics: [
        { key: 'complianceScore', label: 'نسبة الامتثال' },
        { key: 'totalControls', label: 'إجمالي الضوابط' },
        { key: 'verifiedControls', label: 'ضوابط محققة' },
        { key: 'criticalFindings', label: 'نتائج حرجة' },
      ],
      versions: [{ label: 'V1.0', status: 'done' }, { label: 'V2.0', status: 'active' }, { label: 'V3.0', status: 'planned' }],
      dataKey: 'muwafaq',
    },
    {
      id: 2, icon: '🏥', nameAr: 'صحة', subtitleAr: 'طيار المطالبات الصحية',
      color: '#448AFF', light: '#82B1FF', url: 'https://sahha.sanajim.com',
      metrics: [
        { key: 'totalClaims', label: 'إجمالي المطالبات' },
        { key: 'approvedClaims', label: 'مقبولة' },
        { key: 'rejectedClaims', label: 'مرفوضة' },
        { key: 'approvalRate', label: 'نسبة القبول' },
      ],
      versions: [{ label: 'V1.0', status: 'done' }, { label: 'V2.0', status: 'active' }, { label: 'V3.0', status: 'planned' }],
      dataKey: 'sahha',
    },
    {
      id: 3, icon: '👷', nameAr: 'بوابة العمالة', subtitleAr: 'سوق تخصيص العمالة',
      color: '#FF6B35', light: '#FF9E6D', url: '',
      metrics: [
        { key: 'totalCompanies', label: 'شركات' },
        { key: 'activeOffers', label: 'عروض نشطة' },
        { key: 'activeRequests', label: 'طلبات نشطة' },
        { key: 'completedContracts', label: 'عقود' },
      ],
      versions: [{ label: 'Backend', status: 'active' }, { label: 'Flutter', status: 'planned' }, { label: 'Admin', status: 'planned' }],
      dataKey: 'labor',
    },
    {
      id: 4, icon: '🔐', nameAr: 'مطابقة NCA', subtitleAr: 'ضوابط الأمن السيبراني الأساسية',
      color: '#E11D48', light: '#FB7185', url: 'https://muwafaq.sanajim.com',
      metrics: [
        { key: 'complianceScore', label: 'نسبة المطابقة' },
        { key: 'totalControls', label: 'إجمالي الضوابط' },
        { key: 'evidencedControls', label: 'ضوابط محققة' },
        { key: 'criticalFindings', label: 'نتائج حرجة' },
      ],
      versions: [{ label: 'Phase 1', status: 'active' }, { label: 'Phase 2', status: 'planned' }, { label: 'Phase 3', status: 'planned' }],
      dataKey: 'ncaEcc',
    },
    {
      id: 5, icon: '🏎️', nameAr: 'AI Racing Game', subtitleAr: 'لعبة سباق بالذكاء الاصطناعي',
      color: '#FF4081', light: '#FF79B0', url: '',
      metrics: [
        { key: 'progress', label: 'التقدم' },
        { key: 'completedItems', label: 'مهام مكتملة' },
        { key: 'totalItems', label: 'إجمالي المهام' },
        { key: 'sourceLines', label: 'أسطر الكود' },
      ],
      versions: [{ label: 'AI Prototype', status: 'planned' }, { label: 'Game Modes', status: 'planned' }, { label: 'Launch', status: 'planned' }],
      dataKey: 'aiRacingGame',
    },
    {
      id: 6, icon: '📈', nameAr: 'منصة التداول', subtitleAr: 'تداول أسهم سعودية بذكاء اصطناعي',
      color: '#00BCD4', light: '#4DD0E1', url: 'https://trading.sanajim.com/login',
      metrics: [
        { key: 'totalValue', label: 'القيمة الإجمالية' },
        { key: 'totalPL', label: 'الأرباح/الخسائر' },
        { key: 'positions', label: 'المحفظة' },
        { key: 'strategies', label: 'استراتيجيات' },
      ],
      versions: [{ label: 'V1.0', status: 'done' }, { label: 'V2.0', status: 'planned' }, { label: 'Mobile', status: 'planned' }],
      dataKey: 'trading',
      staticData: { totalValue: '1,960 ر.س', totalPL: '-6.42 ر.س', positions: '4 أسهم', strategies: '5 استراتيجيات' },
    },
  ]

  const agentActivities = [
    { icon: '👷', agent: 'Lead Dev Agent', action: '䶮تم حفظ شاشات Flutter للبوابة', time: '٢ د' },
    { icon: '🧪', agent: 'QA Agent', action: 'اكتمال مجموعة اختبارات رفيق', time: '٥ د' },
    { icon: '🧠', agent: 'CTO Agent', action: 'تحسين تحليلات لوحة التحكم', time: '١٠ د' },
    { icon: '🔐', agent: 'Security Agent', action: 'مراجعة ضوابط موافق', time: '١٥ د' },
    { icon: '📊', agent: 'QA Agent', action: 'اختبارات أمنية للبوابة', time: '٢٠ د' },
    { icon: '🏗️', agent: 'Lead Dev Agent', action: 'إنشاء API endpoints جديدة', time: '٢٥ د' },
  ]

  const timeStr = lastUpdate
    ? lastUpdate.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })
    : '—'

  const navSections = [
    { key: 'overview', icon: '◈', label: 'نظرة عامة' },
    { key: 'projects', icon: '📐', label: 'المشاريع' },
    { key: 'agents', icon: '🤖', label: 'الوكلاء' },
    { key: 'finance', icon: '💰', label: 'المالي' },
    { key: 'operations', icon: '🦅', label: 'العمليات', action: () => navigate('/operations') },
  ]

  return (
    <div className="min-h-screen bg-[#07090F] text-[#F1F5F9]">
      {/* ═══ Animated Gradient Header ═══ */}
      <header className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 bg-gradient-to-r from-dashboard/10 via-rafiq/5 to-muwafaq/10" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(139,92,246,0.08) 0%, transparent 70%)',
        }} />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-dashboard to-dashboard-light
                flex items-center justify-center text-lg font-bold text-white shadow-lg shadow-dashboard/20">
                س
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">سناجم تكنولوجيا 🦅</h1>
                <p className="text-[11px] text-[#94A3B8]">مركز القيادة التنفيذي — AI-Agent-Only SaaS</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-[11px] text-[#64748B] bg-white/[0.03] rounded-lg px-3 py-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                مباشر • {timeStr}
              </div>
              <button onClick={loadData}
                className="text-[11px] bg-white/[0.04] hover:bg-white/[0.08] rounded-lg px-3 py-1.5
                  text-[#94A3B8] hover:text-white transition-all">
                تحديث
              </button>
              {/* Link to old dashboard */}
              <a href="/dashboard-legacy"
                className="text-[10px] text-[#475569] hover:text-dashboard transition-colors border border-white/[0.06]
                  rounded-lg px-2.5 py-1.5 hover:border-dashboard/30">
                النسخة القديمة
              </a>
              <button onClick={logout}
                className="text-[11px] text-[#64748B] hover:text-red-400 transition-colors">
                خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ═══ Section Tabs ═══ */}
      <div className="border-b border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex gap-1 py-2 overflow-x-auto">
            {navSections.map(s => (
              <button key={s.key}
                onClick={() => s.action ? s.action() : setActiveSection(s.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium
                  transition-all whitespace-nowrap
                  ${activeSection === s.key
                    ? 'bg-white/[0.06] text-white'
                    : 'text-[#64748B] hover:text-[#94A3B8] hover:bg-white/[0.03]'
                  }`}>
                <span>{s.icon}</span> {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ Main Content ═══ */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ═══ Projects Tab ═══ */}
        {activeSection === 'projects' && <ProjectsSection />}

        {/* ═══ Overview Tab ═══ */}
        {activeSection === 'overview' && (<>

        {/* Hero Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <HeroStat icon="📦" label="مشاريع نشطة" value={activeServices} color="#8B5CF6" delay={0} />
          <HeroStat icon="📄" label="إجمالي الأسطر" value={totalSourceLines} color="#00BFA5" delay={100} />
          <HeroStat icon="✅" label="مهام مكتملة" value={overallStats.done} color="#22C55E" delay={200} />
          <HeroStat icon="🪙" label="Tokens المقدرة" value={Math.round(totalSourceLines * 2)} color="#F59E0B" delay={300} />
        </div>

        {/* Products Grid */}
        <div>
          <h2 className="text-sm font-bold text-[#94A3B8] mb-3 px-1">المنتجات والمشاريع</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRODUCTS.map((p, i) => (
              <ProductCard
                key={p.id}
                product={p}
                health={healthData?.[p.dataKey]}
                data={dashboardData?.[p.dataKey]}
                delay={i * 100}
              />
            ))}
          </div>
        </div>

        {/* Bottom Grid: Activity + Finance + Blockers + Milestones */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AgentFeed activities={agentActivities} delay={400} />
          <FinancialCard delay={500} />
          <BlockersCard delay={600} />
          <MilestonesTimeline delay={700} />
          <ResourceAllocation delay={800} />
          {/* Server Quick Stats */}
          <GlassCard delay={900} className="p-5" glow="#448AFF10">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-base">🖥️</span> حالة السيرفر
            </h3>
            <div className="space-y-3">
              {[
                { label: 'المعالج', value: opsData?.server?.cpu_percent != null ? `${opsData.server.cpu_percent}%` : '—', icon: '⚡' },
                { label: 'الذاكرة', value: opsData?.server?.memory?.display || '—', icon: '🧠' },
                { label: 'القرص', value: opsData?.server?.disk?.display || '—', icon: '💾' },
                { label: 'مدة التشغيل', value: opsData?.server?.uptime || '—', icon: '⏱' },
                { label: 'شهادة SSL', value: opsData?.security?.ssl?.days_left ? `${opsData.security.ssl.days_left} يوم` : '—', icon: '🔒' },
              ].map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{s.icon}</span>
                    <span className="text-[12px] text-[#94A3B8]">{s.label}</span>
                  </div>
                  <span className="text-[13px] font-medium text-white">{s.value}</span>
                </div>
              ))}
            </div>
            <a href="/operations" className="mt-3 flex items-center justify-center gap-1 text-[11px]
              text-dashboard hover:text-dashboard-light transition-colors py-2">
              فتح مركز العمليات ←
            </a>
          </GlassCard>
        </div>

        </> )}

        {/* Footer */}
        <footer className="text-center py-4 text-[10px] text-[#334155]">
          © 2026 Sanajim Technologies • AI-Agent-Only • المملكة العربية السعودية
        </footer>
      </main>
    </div>
  )
}
